import { oc } from "@orpc/contract";
import { z } from "zod";
import { ApiAccessSchema } from "./access.js";
/** What a key may call: `read` reaches the reads, `write` those and the writes (V6 plan §13.3.1); the same enum the procedure meta uses. */
export const ApiKeyAccessSchema = ApiAccessSchema;
/** A key as `v5k_` plus 40 base62 characters: the secret `apiKeys.create` answers once. */
export const API_KEY_SECRET = /^v5k_[0-9A-Za-z]{40}$/;
export const API_KEY_NAME_MAX = 80;
export const API_KEY_EXPIRY_MAX_DAYS = 365;
// One of the account's keys, as the list shows it: never the secret, which only its sha256
// keeps. Runs and cost records name the key that started them (migration 042); the 30-day
// spend column joins this row later (plan row H15).
export const ApiKeySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** What the secret starts with (`v5k_` and its next 8 characters); null on a key issued before prefixes were kept. */
    prefix: z.string().nullable(),
    access: ApiKeyAccessSchema,
    /** The one project the key may reach; null = the whole account. */
    projectId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    lastUsedAt: z.string().datetime().nullable(),
    /** Null = the key never expires. */
    expiresAt: z.string().datetime().nullable(),
    /** When the newest run this key started began; null when it started none. Optional on the wire while clients learn it. */
    lastRunAt: z.string().datetime().nullable().optional(),
});
// Account API keys (V6 plan §13.3.1 "Keys", row H12): a person issues, lists and revokes the
// keys a CLI, a job or another service holds to call /api/v5 for their account on this site.
// Every procedure answers only for the signed-in viewer's own account on the request's site,
// and never lists, revokes or names the server's own engine keys (label `engine:…`).
//
// Callable with the session cookie only (never a key, never a bearer session). A key must not
// mint, list or revoke keys: every procedure here is `keys: false`, which the caller middleware
// enforces once a key reaches /api/v5 (PR H0.3). A bearer session must not either (D71): the
// area is in COOKIE_ONLY_AREAS, so a bearer caller is 403 admin_requires_cookie.
export const apiKeys = {
    list: oc
        .route({
        summary: "The account's API keys",
        description: "Every live key the viewer's account holds on this site, newest first, with its name, prefix, access, project, creation, last use, expiry and newest run; never a secret, never a revoked key, never an engine key. Needs the session cookie only (never a key, never a bearer session).",
    })
        .meta({ access: "read", keys: false })
        .output(z.object({ keys: z.array(ApiKeySchema) })),
    create: oc
        .route({
        summary: "Issue an API key",
        description: "Mints a v5k_ key for the viewer's account on this site, read or write, optionally for one of its projects (someone else's is project_not_found) and optionally expiring after 1 to 365 days. The secret is answered once and only its sha256 is stored, so an Idempotency-Key is not honoured here: a retry mints another key. 10 a minute per account. Needs the session cookie only (never a key, never a bearer session).",
    })
        .meta({ access: "write", keys: false, replays: false })
        .input(z.object({
        name: z.string().min(1).max(API_KEY_NAME_MAX),
        access: ApiKeyAccessSchema,
        projectId: z.string().uuid().optional(),
        expiresInDays: z.number().int().min(1).max(API_KEY_EXPIRY_MAX_DAYS).optional(),
    }))
        .output(z.object({ key: ApiKeySchema, secret: z.string().regex(API_KEY_SECRET) })),
    revoke: oc
        .route({
        summary: "Revoke an API key",
        description: "Revokes one of the account's keys on this site; it fails at its next request. Revoking a revoked key answers revoked again; a key that is not the account's (or is an engine key) is api_key_not_found. Needs the session cookie only (never a key, never a bearer session).",
    })
        .meta({ access: "write", keys: false })
        .input(z.object({ id: z.string().uuid() }))
        .output(z.object({ revoked: z.literal(true) })),
};
//# sourceMappingURL=api-keys.js.map